import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Share2,
  Copy,
  Pencil,
  Trash2,
  Calendar,
  ClipboardList,
  DollarSign,
  Archive,
  Loader2,
  Edit,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { format, parseISO, startOfDay } from "date-fns";
import { ShareDialog } from "@/components/share/ShareDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { ProjectBudget } from "./ProjectBudget";
import { useTranslation } from "react-i18next";

interface Project {
  id: string;
  name: string;
  description: string;
  camera_budget: number;
  ge_budget: number;
  budget_enabled: boolean;
  prep_date: string | null;
  shoot_dates?: string[];
  pre_light_date?: string | null;
  archived_at?: string | null;
  camera_enabled?: boolean;
  ge_enabled?: boolean;
}

interface ProjectCardProps {
  project: Project;
  onNavigate: (id: string) => void;
  onShare: (project: Project) => void;
  onDuplicate: (project: Project) => void;
  onEdit: (project: Project) => void;
  onDelete: (id: string) => void;
  onArchive?: (id: string) => void;
  isArchiving?: boolean;
}

const formatDate = (date: string | null) => {
  if (!date) return null;
  return format(startOfDay(parseISO(date)), "MMM d, yyyy");
};

const parseProjectTitle = (name: string) => {
  const locationSeparators = [" - ", ", ", " | "];

  for (const separator of locationSeparators) {
    if (name.includes(separator)) {
      const [title, location] = name.split(separator);
      return { title: title.trim(), location: location.trim() };
    }
  }

  return { title: name, location: null };
};

export const ProjectCard = ({
  project,
  onNavigate,
  onShare,
  onDuplicate,
  onEdit,
  onDelete,
  onArchive,
  isArchiving = false,
}: ProjectCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const { t } = useTranslation();

  const hasBudget =
    project.budget_enabled &&
    ((project.camera_enabled !== false && project.camera_budget > 0) ||
      (project.ge_enabled !== false && project.ge_budget > 0));
  const isMobile = useIsMobile();

  const { title, location } = parseProjectTitle(project.name);

  const actionButtons = (
    <div className="flex gap-1 flex-shrink-0">
      <Button
        variant="ghost"
        size="icon"
        className={`transition-opacity hover:bg-primary/10 ${
          isMobile ? "h-8 w-8" : "h-8 w-8"
        } opacity-100`}
        onClick={(e) => {
          e.stopPropagation();
          setShowShareDialog(true);
        }}
        title={t("projects.share_project")}
      >
        <Share2 className="h-4 w-4 shrink-0" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className={`transition-opacity hover:bg-primary/10 ${
          isMobile ? "h-8 w-8" : "h-8 w-8"
        } opacity-100`}
        onClick={(e) => {
          e.stopPropagation();
          onDuplicate(project);
        }}
        title={t("projects.duplicate_project")}
      >
        <Copy className="h-4 w-4 shrink-0" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className={`transition-opacity hover:bg-primary/10 ${
          isMobile ? "h-8 w-8" : "h-8 w-8"
        } opacity-100`}
        onClick={(e) => {
          e.stopPropagation();
          onEdit(project);
        }}
        title={t("projects.edit_project")}
      >
        <Pencil className="h-4 w-4 shrink-0" />
      </Button>
      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={`transition-opacity hover:bg-primary/10 ${
              isMobile ? "h-8 w-8" : "h-8 w-8"
            } opacity-100`}
            onClick={(e) => e.stopPropagation()}
            disabled={isArchiving}
            title={t(
              project.archived_at
                ? "projects.unarchive_project"
                : "projects.archive_project"
            )}
          >
            {isArchiving ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            ) : (
              <Archive className="h-4 w-4 shrink-0" />
            )}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="max-w-[90vw] w-[425px] mx-4">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                project.archived_at
                  ? "projects.unarchive_project"
                  : "projects.archive_project"
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {project.archived_at
                ? t("projects.confirm_unarchive", { name: project.name })
                : t("projects.confirm_archive", { name: project.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={isMobile ? "flex-col space-y-2" : ""}>
            <AlertDialogCancel
              onClick={(e) => e.stopPropagation()}
              className={isMobile ? "w-full mt-0" : ""}
            >
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.stopPropagation();
                setShowArchiveDialog(false);
                onArchive?.(project.id);
              }}
              disabled={isArchiving}
              className={isMobile ? "w-full" : ""}
            >
              {isArchiving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("common.processing")}
                </>
              ) : (
                t(
                  project.archived_at
                    ? "projects.unarchive"
                    : "projects.archive"
                )
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={`text-destructive hover:text-destructive hover:bg-destructive/10 ${
              isMobile ? "h-8 w-8" : "h-8 w-8"
            } opacity-100`}
            onClick={(e) => e.stopPropagation()}
            title={t("projects.delete_project")}
          >
            <Trash2 className="h-4 w-4 shrink-0" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="max-w-[90vw] w-[425px] mx-4">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("projects.delete_project")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("projects.confirm_delete", { name: project.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={isMobile ? "flex-col space-y-2" : ""}>
            <AlertDialogCancel
              onClick={(e) => e.stopPropagation()}
              className={isMobile ? "w-full mt-0" : ""}
            >
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.stopPropagation();
                onDelete(project.id);
              }}
              className={`${
                isMobile ? "w-full" : ""
              } bg-destructive hover:bg-destructive/90`}
            >
              {t("projects.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
  return (
    <>
      <Card
        className="group transition-all duration-300 hover:shadow-xl hover:-translate-y-1 glass-card overflow-hidden border-white/10"
        onClick={() => onNavigate(project.id)}
      >
        <CardHeader className="flex flex-col space-y-0 py-3 pb-1">
          <div className="flex items-start justify-between w-full">
            <div className="flex items-start gap-2 w-full">
              <div className="p-1.5 rounded-lg bg-primary/10 shrink-0">
                <ClipboardList className="h-4 w-4 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <div className={isMobile ? "space-y-0.5" : ""}>
                  <h3
                    className={`font-semibold group-hover:text-primary transition-colors font-brakle break-words pr-4 
                    ${
                      isMobile
                        ? "text-base leading-tight line-clamp-2"
                        : "text-lg"
                    }`}
                    style={{
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {location ? title : project.name}
                  </h3>
                  {location && (
                    <div
                      className={`text-muted-foreground ${
                        isMobile
                          ? "text-xs font-normal leading-snug"
                          : "text-sm"
                      }`}
                      style={{
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {location}
                    </div>
                  )}
                </div>
                {project.description && (
                  <div
                    className={`text-xs text-muted-foreground ${
                      isMobile ? "mt-1" : "mt-0.5"
                    }`}
                  >
                    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
                      <CollapsibleContent
                        className={isExpanded ? "" : "line-clamp-1"}
                        style={{
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        {project.description}
                      </CollapsibleContent>
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 h-auto text-primary hover:no-underline text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsExpanded(!isExpanded);
                          }}
                        >
                          {isExpanded ? t("show_less") : t("show_more")}
                        </Button>
                      </CollapsibleTrigger>
                    </Collapsible>
                  </div>
                )}
              </div>
            </div>
            {!isMobile && actionButtons}
          </div>

          {isMobile && <div className="mt-2 ml-8">{actionButtons}</div>}
        </CardHeader>
        <CardContent className="pt-1 pb-3">
          <div className="space-y-2">
            <div className="flex flex-wrap gap-1.5">
              {project.prep_date && (
                <Badge
                  variant="secondary"
                  className="bg-[#4B7BF5] hover:bg-[#4B7BF5]/90 text-xs py-0.5"
                >
                  <Calendar className="h-3 w-3 mr-1" />
                  <span className="truncate">
                    {t("prep")}: {formatDate(project.prep_date)}
                  </span>
                </Badge>
              )}
              {project.pre_light_date && (
                <Badge
                  variant="secondary"
                  className="bg-[#9B87F5] hover:bg-[#9B87F5]/90 text-xs py-0.5"
                >
                  <Calendar className="h-3 w-3 mr-1" />
                  <span className="truncate">
                    {t("pre_light")}: {formatDate(project.pre_light_date)}
                  </span>
                </Badge>
              )}
              {project.shoot_dates && project.shoot_dates.length > 0 && (
                <Badge
                  variant="secondary"
                  className="bg-[#4CAF50] hover:bg-[#4CAF50]/90 text-xs py-0.5"
                >
                  <Calendar className="h-3 w-3 mr-1" />
                  <span className="truncate">
                    {t("shoot")}:{" "}
                    {project.shoot_dates.map((date, index) => (
                      <span key={date}>
                        {formatDate(date)}
                        {index < project.shoot_dates!.length - 1 ? ", " : ""}
                      </span>
                    ))}
                  </span>
                </Badge>
              )}
            </div>

            {project.budget_enabled && (
              <div className="space-y-1">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1 text-xs">
                    <DollarSign className="h-3.5 w-3.5 text-primary shrink-0" />
                    <span className="font-medium">{t("budget")}</span>
                  </div>
                  {!hasBudget && (
                    <div className="flex items-center text-xs text-muted-foreground/70">
                      <span>{t("not_set")}</span>
                      <Edit
                        className="h-3 w-3 ml-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(project);
                        }}
                      />
                    </div>
                  )}
                </div>

                {hasBudget && (
                  <div className="grid grid-cols-2 gap-2">
                    <ProjectBudget
                      cameraBudget={project.camera_budget}
                      geBudget={project.ge_budget}
                      cameraEnabled={project.camera_enabled}
                      geEnabled={project.ge_enabled}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <ShareDialog
        projectId={project.id}
        open={showShareDialog}
        onOpenChange={setShowShareDialog}
      />
    </>
  );
};
